import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import aboutImage from "../assets/Blue-intro.jpg";

const SUPPORT_WHATSAPP_NUMBER = "919080432374";
const SUPPORT_MESSAGE = "Hi, I am Looking for Cyber Security Training";

const impactStats = [
  {
    value: "1 Lakh+",
    label: "Cyber Warriors Vision",
    description: "Preparing skilled cybersecurity professionals by 2030.",
  },
  {
    value: "100%",
    label: "Practical Learning",
    description: "Hands-on labs, real tools, guided practice, and live use cases.",
  },
  {
    value: "24/7",
    label: "Learning Support",
    description: "Community-driven support for doubts, practice, and career guidance.",
  },
  {
    value: "2 Years",
    label: "Recording Access",
    description: "Long-term access to recordings for revision and continued learning.",
  },
];

const learningPillars = [
  {
    icon: "fa-solid fa-shield-halved",
    title: "Real-World Cybersecurity Training",
    description:
      "Our programs are designed around real application security, VAPT, API security, network security, and modern offensive security workflows.",
  },
  {
    icon: "fa-solid fa-laptop-code",
    title: "Hands-On Labs & Tools",
    description:
      "Students work with tools like Burp Suite, Nmap, Wireshark, Metasploit, OWASP ZAP, SQLmap, and practical vulnerable labs.",
  },
  {
    icon: "fa-solid fa-user-tie",
    title: "Career-Focused Mentorship",
    description:
      "We focus on resume building, interview preparation, mock interviews, job readiness, reporting skills, and real-world assessment mindset.",
  },
  {
    icon: "fa-solid fa-people-group",
    title: "Community-Based Learning",
    description:
      "Students learn with mentors, peers, group discussions, support channels, doubt clarification, and ongoing cybersecurity updates.",
  },
];

const values = [
  {
    title: "Practical First",
    description:
      "We believe cybersecurity cannot be mastered only through theory. Every concept must be practiced in labs and real-world scenarios.",
  },
  {
    title: "Industry Relevance",
    description:
      "Our curriculum is aligned with practical AppSec, VAPT, API security, OWASP, reporting, and modern security testing expectations.",
  },
  {
    title: "Student Success",
    description:
      "We guide learners from fundamentals to confidence, helping them understand tools, vulnerabilities, reports, and interview expectations.",
  },
  {
    title: "Accessible Learning",
    description:
      "We make cybersecurity learning structured, affordable, practical, and accessible for students, professionals, and career switchers.",
  },
];

const programs = [
  {
    title: "Junior Penetration Tester",
    level: "Entry-Level Program",
    description:
      "Designed for students and beginners who want to build strong foundations in cybersecurity, networking, OWASP, tools, and VAPT basics.",
    route: "/junior-penetration-tester",
    theme: "from-blue-700 to-cyan-500",
  },
  {
    title: "Senior Penetration Tester",
    level: "Advanced Program",
    description:
      "Designed for learners who want to go deeper into advanced VAPT, API security, Android security, thick client testing, and real-world assessments.",
    route: "/senior-penetration-tester",
    theme: "from-red-600 via-purple-700 to-blue-700",
  },
];

function AboutUs() {
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    document.title = "About Us | Practical Infosec";

    function handleScroll() {
      setShowBackToTop(window.scrollY > 300);
    }

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  function openWhatsAppChat() {
    const encoded = encodeURIComponent(SUPPORT_MESSAGE);
    const whatsappUrl = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encoded}`;

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  }

  function openWhatsAppAppFallback() {
    const encoded = encodeURIComponent(SUPPORT_MESSAGE);
    const deepLink = `whatsapp://send?phone=${SUPPORT_WHATSAPP_NUMBER}&text=${encoded}`;
    const webLink = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encoded}`;

    window.location.href = deepLink;

    setTimeout(() => {
      window.open(webLink, "_blank", "noopener,noreferrer");
    }, 700);
  }

  return (
    <main className="overflow-hidden bg-white">
      {/* HERO */}
      <section className="relative px-4 py-20 overflow-hidden bg-slate-950 md:px-20 md:py-24">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.36),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(14,165,233,0.26),transparent_36%),radial-gradient(circle_at_center,rgba(124,58,237,0.18),transparent_45%)]"></div>
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.045)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.045)_1px,transparent_1px)] bg-[size:44px_44px] opacity-30"></div>
          <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/70 to-transparent"></div>
        </div>

        <div className="relative z-10 grid items-center gap-12 mx-auto max-w-7xl lg:grid-cols-[1fr_0.95fr]">
          <div>
            <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest uppercase border rounded-full text-cyan-300 bg-white/10 border-white/15 backdrop-blur">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              About Practical Infosec
            </span>

            <h1 className="mt-6 text-4xl font-black leading-tight tracking-tight text-white md:text-6xl">
              Building India&apos;s Next Generation of{" "}
              <span className="text-transparent bg-gradient-to-r from-cyan-300 via-blue-300 to-purple-300 bg-clip-text">
                Cyber Warriors
              </span>
            </h1>

            <p className="max-w-3xl mt-6 text-lg font-semibold leading-8 text-slate-300">
              Practical InfoSec Pvt Ltd is a purpose-driven cybersecurity EdTech
              platform focused on real-world, hands-on training that bridges the
              gap between academic learning and industry-ready cybersecurity
              skills.
            </p>

            <div className="flex flex-col gap-4 mt-8 sm:flex-row">
              <Link
                to="/junior-penetration-tester#demoRegistration"
                className="inline-flex items-center justify-center py-4 text-base font-black text-white transition shadow-xl px-7 rounded-2xl bg-gradient-to-r from-blue-700 to-cyan-500 shadow-blue-700/30 hover:scale-105"
              >
                Start Junior Program
              </Link>

              <Link
                to="/senior-penetration-tester#demoRegistration"
                className="inline-flex items-center justify-center py-4 text-base font-black text-white transition border px-7 rounded-2xl border-white/20 bg-white/10 hover:bg-white hover:text-slate-950"
              >
                Explore Senior Program
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-10 md:grid-cols-4">
              {impactStats.map((stat) => (
                <div
                  key={stat.label}
                  className="p-4 border rounded-2xl bg-white/10 border-white/15 backdrop-blur"
                >
                  <p className="text-2xl font-black text-white">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-xs font-bold leading-5 text-slate-300">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-5 rounded-[2rem] bg-gradient-to-r from-blue-500/40 via-cyan-400/30 to-purple-500/30 blur-2xl"></div>

            <div className="relative overflow-hidden border shadow-2xl rounded-[2rem] border-white/15 bg-white/10 backdrop-blur">
              <div className="p-3 bg-slate-950/70">
                <img
                  src={aboutImage}
                  alt="Practical Infosec Cybersecurity Learning Platform"
                  className="object-contain w-full h-auto rounded-[1.5rem]"
                />
              </div>

              <div className="p-6 border-t bg-slate-950/95 border-white/10">
                <p className="text-xs font-black tracking-widest uppercase text-cyan-300">
                  Mission 2030
                </p>

                <h2 className="mt-2 text-2xl font-black text-white">
                  Preparing 1 Lakh Cyber Warriors
                </h2>

                <p className="mt-3 text-sm font-semibold leading-6 text-slate-300">
                  A long-term mission to transform cybersecurity learning into a
                  practical, accessible, and career-focused journey.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="relative px-4 py-20 overflow-hidden bg-white md:px-20">
        <div className="absolute top-0 right-0 rounded-full w-96 h-96 bg-blue-200/30 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 rounded-full w-96 h-96 bg-cyan-200/20 blur-3xl"></div>

        <div className="relative z-10 grid items-center gap-12 mx-auto max-w-7xl lg:grid-cols-[0.9fr_1fr]">
          <div className="relative">
            <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-r from-blue-200 via-cyan-100 to-purple-200 blur-2xl"></div>

            <div className="relative p-4 bg-white border shadow-2xl rounded-[2rem] border-slate-200">
              <img
                src={aboutImage}
                alt="Cybersecurity Illustration"
                className="w-full h-auto rounded-[1.5rem]"
              />

              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="p-4 border rounded-2xl bg-slate-50 border-slate-200">
                  <p className="text-xl font-black text-blue-700">Live</p>
                  <p className="mt-1 text-xs font-bold text-slate-600">
                    Instructor-led training
                  </p>
                </div>

                <div className="p-4 border rounded-2xl bg-slate-50 border-slate-200">
                  <p className="text-xl font-black text-cyan-700">Labs</p>
                  <p className="mt-1 text-xs font-bold text-slate-600">
                    Practical cybersecurity practice
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest text-blue-700 uppercase bg-blue-100 border border-blue-200 rounded-full">
              Our Mission
            </span>

            <h2 className="mt-5 text-4xl font-black tracking-tight text-slate-950 md:text-5xl">
              Preparing 1 Lakh Cyber Warriors —{" "}
              <span className="text-blue-700">By 2030</span>
            </h2>

            <p className="mt-6 text-lg font-semibold leading-8 text-slate-700">
              At <strong>Practical InfoSec Pvt Ltd.</strong>, we are on a
              mission to redefine cybersecurity education. As a purpose-driven
              EdTech platform, we provide real-world, hands-on cybersecurity
              training designed to bridge the critical gap between academic
              theory and industry-ready skills.
            </p>

            <p className="mt-5 text-lg font-semibold leading-8 text-slate-700">
              Our goal is simple yet ambitious: to transform 1 lakh careers by
              2030 by equipping aspiring professionals, students, and
              career-changers with the practical knowledge, tools, and confidence
              needed to start or elevate their journey in the cybersecurity
              domain.
            </p>

            <p className="mt-5 text-lg font-semibold leading-8 text-slate-700">
              We believe cybersecurity learning should be practical, accessible,
              and impactful — and we’re building a future where anyone with
              passion and persistence can thrive in this rapidly evolving field.
            </p>
          </div>
        </div>
      </section>

      {/* IMPACT STATS */}
      <section className="px-4 py-20 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 md:px-20">
        <div className="max-w-6xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest uppercase border rounded-full text-cyan-300 bg-white/10 border-white/15">
            Our Impact Model
          </span>

          <h2 className="mt-5 text-4xl font-black tracking-tight text-white md:text-5xl">
            Built for Practical Cybersecurity Outcomes
          </h2>

          <p className="max-w-3xl mx-auto mt-5 text-lg font-semibold leading-8 text-slate-300">
            We combine structured learning, live mentorship, cybersecurity labs,
            tools, reporting, and interview preparation to help learners become
            industry-ready.
          </p>

          <div className="grid grid-cols-1 gap-5 mt-12 sm:grid-cols-2 lg:grid-cols-4">
            {impactStats.map((stat) => (
              <div
                key={stat.label}
                className="p-6 text-left transition border rounded-3xl bg-white/10 border-white/15 backdrop-blur hover:-translate-y-2 hover:bg-white hover:text-slate-950"
              >
                <p className="text-4xl font-black text-cyan-300">
                  {stat.value}
                </p>

                <h3 className="mt-4 text-xl font-black text-white group-hover:text-slate-950">
                  {stat.label}
                </h3>

                <p className="mt-3 text-sm font-semibold leading-6 text-slate-300">
                  {stat.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEARNING PILLARS */}
      <section className="relative px-4 py-20 overflow-hidden bg-slate-50 md:px-20">
        <div className="absolute top-0 left-0 rounded-full w-96 h-96 bg-blue-200/30 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 rounded-full w-96 h-96 bg-purple-200/20 blur-3xl"></div>

        <div className="relative z-10 mx-auto max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest text-blue-700 uppercase bg-blue-100 border border-blue-200 rounded-full">
              Why We Exist
            </span>

            <h2 className="mt-5 text-4xl font-black tracking-tight text-slate-950 md:text-5xl">
              We Bridge the Gap Between Learning and Real Security Work
            </h2>

            <p className="mt-5 text-lg font-semibold leading-8 text-slate-600">
              Most learners understand theory but struggle with practical tools,
              real scenarios, reporting, and interviews. Practical Infosec is
              built to solve that gap.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 mt-12 md:grid-cols-2 xl:grid-cols-4">
            {learningPillars.map((pillar) => (
              <div
                key={pillar.title}
                className="p-6 transition bg-white border shadow-lg group rounded-3xl border-slate-200 hover:-translate-y-2 hover:border-blue-300 hover:shadow-2xl"
              >
                <div className="flex items-center justify-center w-16 h-16 text-2xl text-white transition rounded-2xl bg-gradient-to-br from-blue-700 to-cyan-500 group-hover:scale-110">
                  <i className={pillar.icon}></i>
                </div>

                <h3 className="mt-6 text-xl font-black text-slate-950">
                  {pillar.title}
                </h3>

                <p className="mt-3 text-sm font-semibold leading-6 text-slate-600">
                  {pillar.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="px-4 py-20 bg-white md:px-20">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-5 mb-10 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest text-blue-700 uppercase bg-blue-100 border border-blue-200 rounded-full">
                Our Training Tracks
              </span>

              <h2 className="mt-5 text-4xl font-black tracking-tight text-slate-950 md:text-5xl">
                Cybersecurity Programs Built for Career Growth
              </h2>

              <p className="max-w-3xl mt-5 text-lg font-semibold leading-8 text-slate-600">
                Choose your learning path based on your current level, goals,
                and cybersecurity career direction.
              </p>
            </div>

            <Link
              to="/blog"
              className="inline-flex items-center justify-center px-6 py-3 text-sm font-black text-white transition bg-slate-950 rounded-xl hover:bg-blue-700"
            >
              Read Security Blogs →
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {programs.map((program) => (
              <div
                key={program.title}
                className="relative overflow-hidden transition border shadow-xl rounded-3xl border-slate-200 hover:-translate-y-2 hover:shadow-2xl"
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${program.theme} opacity-95`}
                ></div>

                <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:36px_36px] opacity-30"></div>

                <div className="relative z-10 p-7 md:p-8">
                  <p className="text-xs font-black tracking-widest uppercase text-white/80">
                    {program.level}
                  </p>

                  <h3 className="mt-3 text-3xl font-black text-white">
                    {program.title}
                  </h3>

                  <p className="mt-4 text-base font-semibold leading-7 text-white/90">
                    {program.description}
                  </p>

                  <Link
                    to={`${program.route}#demoRegistration`}
                    className="inline-flex items-center justify-center px-6 py-3 mt-6 text-sm font-black transition bg-white rounded-xl text-slate-950 hover:scale-105"
                  >
                    Apply Now →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="relative px-4 py-20 overflow-hidden bg-slate-50 md:px-20">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest text-blue-700 uppercase bg-blue-100 border border-blue-200 rounded-full">
              Our Core Values
            </span>

            <h2 className="mt-5 text-4xl font-black tracking-tight text-slate-950 md:text-5xl">
              What Drives Practical Infosec
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 mt-12 md:grid-cols-2">
            {values.map((value, index) => (
              <div
                key={value.title}
                className="transition bg-white border shadow-lg p-7 rounded-3xl border-slate-200 hover:-translate-y-2 hover:border-blue-300 hover:shadow-2xl"
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 text-lg font-black text-white rounded-2xl bg-slate-950">
                    {String(index + 1).padStart(2, "0")}
                  </div>

                  <h3 className="text-2xl font-black text-slate-950">
                    {value.title}
                  </h3>
                </div>

                <p className="mt-4 text-base font-semibold leading-7 text-slate-600">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-4 py-20 bg-white md:px-20">
        <div className="relative max-w-6xl p-8 mx-auto overflow-hidden shadow-2xl rounded-[2rem] bg-gradient-to-r from-slate-950 via-blue-950 to-slate-950 md:p-12">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,165,233,0.25),transparent_35%)]"></div>

          <div className="relative z-10 grid items-center gap-8 lg:grid-cols-[1fr_auto]">
            <div>
              <p className="text-xs font-black tracking-widest uppercase text-cyan-300">
                Join the Mission
              </p>

              <h2 className="mt-3 text-3xl font-black text-white md:text-5xl">
                Start your cybersecurity journey with practical training.
              </h2>

              <p className="max-w-3xl mt-4 text-base font-semibold leading-7 text-slate-300">
                Learn with structured live classes, hands-on labs, mentor
                support, practical tools, recordings, community support, and
                career-focused guidance.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
              <Link
                to="/junior-penetration-tester#demoRegistration"
                className="inline-flex items-center justify-center py-4 text-base font-black text-white transition px-7 rounded-2xl bg-gradient-to-r from-blue-700 to-cyan-500 hover:scale-105"
              >
                Apply for Junior
              </Link>

              <Link
                to="/senior-penetration-tester#demoRegistration"
                className="inline-flex items-center justify-center py-4 text-base font-black text-white transition border px-7 rounded-2xl border-white/20 bg-white/10 hover:bg-white hover:text-slate-950"
              >
                Apply for Senior
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHATSAPP STICKY BUTTON */}
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setWhatsappOpen((prev) => !prev);
        }}
        className="fixed z-50 p-3 px-4 text-white transition -translate-y-1/2 rounded-full shadow-lg cursor-pointer top-1/2 right-6 animate-blink hover:opacity-90 bg-gradient-to-r from-green-400 via-green-500 to-green-600"
        aria-label="Join WhatsApp"
      >
        <i className="text-4xl fa-brands fa-whatsapp"></i>
      </button>

      {/* WHATSAPP MODAL */}
      {whatsappOpen && (
        <div
          className="fixed z-50 p-4 transition-opacity duration-300 -translate-y-1/2 bg-white shadow-xl top-1/2 right-20 rounded-xl w-72 max-sm:right-4 max-sm:left-4 max-sm:w-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="whatsappTitle"
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setWhatsappOpen(false);
            }}
            className="absolute text-3xl text-gray-600 cursor-pointer top-2 left-4 hover:text-red-500"
            aria-label="Close"
          >
            &times;
          </button>

          <h2
            id="whatsappTitle"
            className="mt-6 mb-2 text-lg font-bold text-center text-gray-800"
          >
            Unsure about which course to choose?
          </h2>

          <p className="mb-3 text-sm text-center text-gray-700">
            Tap below to start a WhatsApp chat — we&apos;re here to help!
          </p>

          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={openWhatsAppChat}
              className="inline-flex items-center justify-center w-full gap-2 px-3 py-2 text-sm text-white transition bg-green-600 rounded-full hover:bg-green-700"
              aria-label="Chat Now on WhatsApp"
            >
              <i className="fa-brands fa-whatsapp"></i>
              Chat Now
            </button>

            <button
              type="button"
              onClick={openWhatsAppAppFallback}
              className="inline-flex items-center justify-center w-full gap-2 px-3 py-2 text-sm text-gray-800 transition border border-gray-200 rounded-full hover:bg-gray-50"
              aria-label="Open in WhatsApp App"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-green-600"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M20.52 3.48A11.93 11.93 0 0012 .5C5.73.5.99 5.24.99 11.5c0 2.01.53 3.95 1.53 5.68L0 24l6.97-1.79c1.61.45 3.28.68 4.9.68 6.27 0 11.01-4.74 11.01-11 0-3.02-1.18-5.85-3.36-7.41zM12 21.5c-1.44 0-2.86-.34-4.14-.99l-.3-.15-4.14 1.06 1.15-3.98-.18-.31A8.12 8.12 0 013 11.5c0-4.97 4.06-9 9-9 2.41 0 4.66.94 6.36 2.64a8.95 8.95 0 012.64 6.36c0 4.97-4.06 9-9 9z" />
              </svg>
              Open in App
            </button>
          </div>
        </div>
      )}

      {/* BACK TO TOP BUTTON */}
      {showBackToTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed z-50 p-4 text-white transition bg-blue-700 rounded-lg shadow-lg cursor-pointer bottom-12 right-7 hover:bg-blue-800"
          aria-label="Back to top"
        >
          <i className="text-xl fa-solid fa-arrow-up"></i>
        </button>
      )}

      {/* FLOATING CALL BUTTON */}
      <a
        href="tel:+91-9080432374"
        className="fixed z-50 p-3 text-white transition rounded-md shadow-lg cursor-pointer bg-violet-600 bottom-60 right-7 hover:bg-green-700"
        aria-label="Call Us"
      >
        <i className="text-2xl fa-solid fa-phone"></i>
      </a>
    </main>
  );
}

export default AboutUs;